import { useBreakpoint } from '@orn/shared-web/hooks/useBreakpoint';
import { cx } from '@orn/shared-web/utils/cx';
import * as React from 'react';
import { Outlet, useNavigate } from 'react-router-dom';

import AlertsCenter from '../components/AlertsCenter';
import PaneCard, { PANE_SCROLLER_CLASSNAME } from '../components/entity/PaneCard';
import ImpersonatorNotice from '../components/ImpersonatorNotice';
import {
  ICON_RAIL_WIDTH_PX,
  MAIN_SIDEBAR_WIDTH_PX,
  MOBILE_TOP_BAR_HEIGHT_PX,
} from '../components/navigation/dimensions';
import MainSidebar from '../components/navigation/MainSidebar';
import SyncingNotice from '../components/SyncingNotice';
import { useOnboardingState } from '../hooks/queries/useOnboardingState';
import { useHeaderScrollProgress } from '../hooks/useHeaderScrollProgress';
import { useTransitionedPresence, type PresenceStage } from '../hooks/useTransitionedPresence';
import AssistantPane from '../panes/assistant/AssistantPane';
import { PATHS } from '../paths';
import { SIDE_PANEL_TRANSITION_MS, useSidePanel, type SidePanelContent } from '../providers/SidePanelProvider';

/** The side panel's width floor. */
const SIDE_PANEL_WIDTH_PX = 460;
/** The side panel's width ceiling (Figma: "Assistant: 40% • Max-Width: 640px"). */
const SIDE_PANEL_MAX_WIDTH_PX = 640;
/** The side panel's fluid share of the row (Figma: "Assistant: 40%"). */
const SIDE_PANEL_WIDTH_FRACTION = 0.4;

/**
 * Inline duration for every panel transition, derived from
 * {@link SIDE_PANEL_TRANSITION_MS} so the CSS transitions and the presence
 * hook's settle-timeout fallback cannot desync. A `duration-*` class literal
 * would restate the number, and Tailwind cannot build one from a variable.
 */
const SIDE_PANEL_TRANSITION_DURATION_STYLE = {
  transitionDuration: `${SIDE_PANEL_TRANSITION_MS}ms`,
} as const;

/**
 * The pixel width the open column settles at — the fluid 40%-of-row value under
 * its clamps, resolved against the row's content box (the row carries the nav
 * offset as padding). Used as the explicit endpoint while the width transition
 * runs: the fluid `w-2/5` + `minWidth` pair cannot transition to zero, because
 * the 460px floor holds the *used* width while only the specified 40% animates.
 */
const measureOpenPanelWidthPx = (columnEl: HTMLElement): number => {
  const row = columnEl.parentElement;
  if (!row) {
    return SIDE_PANEL_WIDTH_PX;
  }

  const rowStyle = window.getComputedStyle(row);
  const rowContentWidth = row.clientWidth - parseFloat(rowStyle.paddingLeft) - parseFloat(rowStyle.paddingRight);
  const fluidWidth = rowContentWidth * SIDE_PANEL_WIDTH_FRACTION;
  return Math.min(Math.max(fluidWidth, SIDE_PANEL_WIDTH_PX), SIDE_PANEL_MAX_WIDTH_PX);
};

interface ISidePanelProps {
  content: SidePanelContent;
  /** Where the column is in its enter/exit transition; `open` when at rest. */
  stage: PresenceStage;
  /** Settles the column's width transition — ends the enter, unmounts after the exit. */
  onWidthTransitionEnd: React.TransitionEventHandler;
  /** Whether `feat-assistant` passes — the hidden mount below is gated on it. */
  isAssistantEnabled: boolean;
  /** Collapses the column back to the assistant thread. */
  onCloseAlerts: () => void;
  /** The assistant header's close button — hides the column entirely. */
  onCloseAssistant: () => void;
}

/**
 * The column beside content at `xl+`, seating one of two surfaces in the same
 * chromeless treatment: no border, radius, or shadow, with the column's own
 * insets, fluid at 40% of the row between a 460px floor and a 640px ceiling
 * (Figma node 8052:249487; annotation "Assistant: 40% • Max-Width: 640px").
 * The assistant sits directly on the app ground; the Alerts Center carries the
 * lighter `bg-app_light` ground its design gives it (Figma node 8204:531483).
 *
 * `AssistantPane` is hidden rather than unmounted while the Alerts Center has
 * the column. It keeps the single-WebSocket invariant either way — this is the
 * one mount site — but unmounting would also discard the composer draft and any
 * parked client tool, on a swap the user makes constantly.
 *
 * Hidden still means mounted, though, so the gate has to be checked here rather
 * than left to `visible`: a viewer with the Alerts Center but not the assistant
 * reaches this panel through the center, and an ungated mount would open the
 * assistant's WebSocket behind `hidden` for someone the gate excludes.
 *
 * Motion: the column's open and close animate its real width (the dashboard
 * reflows beside it, matching the design video's continuous chart re-layout)
 * while the content fades; the Alerts Center rides an opaque sheet that slides
 * in from the column's left edge over the still-mounted assistant. Both run
 * {@link SIDE_PANEL_TRANSITION_MS} with `ease-in-out`, and collapse to today's
 * instant swaps under `prefers-reduced-motion`. While a width transition runs,
 * the column and its content are pinned to explicit pixel widths so the content
 * clips instead of reflowing; at rest the fluid clamp returns.
 */
const SidePanel = ({
  content,
  stage,
  onWidthTransitionEnd,
  isAssistantEnabled,
  onCloseAlerts,
  onCloseAssistant,
}: ISidePanelProps) => {
  const columnRef = React.useRef<HTMLDivElement>(null);
  const [pinnedWidthPx, setPinnedWidthPx] = React.useState<number | null>(null);

  React.useLayoutEffect(() => {
    const columnEl = columnRef.current;
    if (!columnEl) {
      return;
    }

    if (stage === 'preEnter') {
      setPinnedWidthPx(measureOpenPanelWidthPx(columnEl));
    } else if (stage === 'preExit') {
      setPinnedWidthPx(columnEl.offsetWidth);
    } else if (stage === 'open') {
      setPinnedWidthPx(null);
    }
  }, [stage]);

  // The sheet's own presence: mounted through its slide-out so the exit can
  // play, already `open` when the whole column mounts directly onto alerts
  // (the bell pressed while the column was closed — the column's width enter
  // is the only motion that case wants).
  const alertsSheet = useTransitionedPresence(content === 'alerts' ? content : null, {
    transitionProperty: 'translate',
    durationMs: SIDE_PANEL_TRANSITION_MS,
  });
  const isSheetParkedLeft = alertsSheet.stage === 'preEnter' || alertsSheet.stage === 'exiting';

  // `preExit` before its measurement has landed still renders the at-rest
  // fluid width: the measuring layout effect must read the true open width,
  // and an interim `width: 0` frame would start the transition a frame early
  // from the wrong side.
  const isColumnAtRest = stage === 'open' || (stage === 'preExit' && pinnedWidthPx === null);
  const isColumnCollapsed = stage === 'preEnter' || stage === 'exiting';

  return (
    <div
      ref={columnRef}
      onTransitionEnd={onWidthTransitionEnd}
      style={{
        ...(isColumnAtRest
          ? { minWidth: SIDE_PANEL_WIDTH_PX, maxWidth: SIDE_PANEL_MAX_WIDTH_PX }
          : { width: isColumnCollapsed ? 0 : (pinnedWidthPx ?? 0) }),
        ...SIDE_PANEL_TRANSITION_DURATION_STYLE,
      }}
      className={cx(
        'flex h-full shrink-0 flex-col overflow-hidden',
        'transition-[width] ease-in-out motion-reduce:transition-none',
        isColumnAtRest && 'w-2/5'
      )}
    >
      <div
        style={{
          ...(pinnedWidthPx !== null ? { width: pinnedWidthPx } : undefined),
          ...SIDE_PANEL_TRANSITION_DURATION_STYLE,
        }}
        className={cx(
          'relative flex h-full min-h-0 flex-col overflow-hidden',
          pinnedWidthPx === null && 'w-full',
          'transition-opacity ease-in-out motion-reduce:transition-none',
          isColumnCollapsed ? 'opacity-0' : 'opacity-100'
        )}
      >
        {isAssistantEnabled && (
          <div
            // Behind the settled sheet the assistant leaves the layout exactly
            // as before; while the sheet slides it stays painted (the sheet
            // covers or reveals it) but must not catch pointer or reader focus.
            aria-hidden={alertsSheet.renderValue !== null || undefined}
            className={cx(
              'flex min-h-0 flex-1 flex-col py-4 pl-4 pr-2',
              alertsSheet.renderValue !== null && (alertsSheet.stage === 'open' ? 'hidden' : 'pointer-events-none')
            )}
          >
            <AssistantPane onClose={onCloseAssistant} />
          </div>
        )}
        {alertsSheet.renderValue !== null && (
          <div
            onTransitionEnd={alertsSheet.onTransitionEnd}
            style={SIDE_PANEL_TRANSITION_DURATION_STYLE}
            // The same chromeless full-column treatment as the assistant, on
            // the lighter ground the design gives this surface (Figma node
            // 8204:531483 — bg-app_light; no border, radius, or shadow).
            className={cx(
              'bg-app_light absolute inset-0 z-10 flex flex-col py-4 pl-4 pr-2',
              'transition-transform ease-in-out motion-reduce:transition-none',
              isSheetParkedLeft ? '-translate-x-full' : 'translate-x-0'
            )}
          >
            <AlertsCenter onClose={onCloseAlerts} />
          </div>
        )}
      </div>
    </div>
  );
};

const HomeScene = () => {
  const navigate = useNavigate();
  const { scrollerRef, onScroll } = useHeaderScrollProgress();

  // The panel docks beside content only at `xl+`. Below that there is no room
  // for it — the nav button and the bell navigate to `/assistant` and `/alerts`
  // instead (see MainApp's route gates). `isXl` here is the same signal that
  // gates those redirects, so the panel and the routes never both mount a
  // surface (and, for the assistant, its single WebSocket).
  const { visible, panelPresence, isAssistantEnabled, showAssistant, toggleAssistant } = useSidePanel();
  const isXl = useBreakpoint('xl');

  // `panelPresence` (from the provider, so `useContentColumnsBreakpoint` flips
  // with the column the user sees) retains the occupant while the exit plays —
  // keeping `AssistantPane` and its WebSocket mounted until the collapse
  // finishes. The render still re-checks `isXl` even though the provider's
  // presence input folds it in: a downward crossing must unmount instantly, or
  // the exit window could double-mount the pane against the `/assistant` route.
  // Only in-`xl` toggles animate.
  const panelInColumn = isXl ? panelPresence.renderValue : null;

  // Narrowing past `xl` takes the column away, so the Alerts Center has to
  // become its route or it would vanish mid-read with no way back to it — the
  // panel is the only thing holding that state, and the URL is on `/dashboard`.
  // `AlertsRouteGate` does the mirror image on the way back up, and each
  // crossing fires once, so the two do not chase each other.
  //
  // The assistant is deliberately left alone: it has behaved this way since
  // before the column was shared, and following it down would change a
  // long-standing behavior this surface has no claim on.
  const wasXl = React.useRef(isXl);
  React.useEffect(() => {
    const crossedBelowXl = wasXl.current && !isXl;
    wasXl.current = isXl;

    if (crossedBelowXl && visible === 'alerts') {
      void navigate(PATHS.ALERTS, { replace: true });
    }
  }, [isXl, visible, navigate]);

  const { needsOnboarding, loading: onboardingStateLoading } = useOnboardingState();

  React.useEffect(() => {
    if (!onboardingStateLoading && needsOnboarding) {
      void navigate(PATHS.ONBOARDING);
    }
  }, [onboardingStateLoading, needsOnboarding, navigate]);

  // Hold the shell until the check resolves and the about-to-redirect window
  // closes — otherwise the main app flashes in before the navigate-effect
  // above lands an onboarding-needing viewer at `/onboarding`.
  if (onboardingStateLoading || needsOnboarding) {
    return null;
  }

  return (
    <>
      <MainSidebar />
      {/*
       * Outer wrapper reserves space for the fixed `MainSidebar` via padding
       * (top bar height below `md`, icon rail at `md`, full sidebar at `lg+`).
       * `flex-1` covers the case where this scene mounts inside a flex-row
       * parent — notably the Storybook global decorator
       * (`bg-app flex h-full w-full`). Without it the wrapper would shrink to
       * its content's intrinsic width and leave a right-side gutter at wide
       * viewports. In production the scene mounts directly inside `#root`
       * (block context, no flex parent), where `flex-1` is a no-op and
       * `h-full` + block-level width handle layout — but keeping `flex-1`
       * makes the layout robust to either context. `min-w-0` lets the inner
       * content shrink below intrinsic min-width so panes can reflow at
       * narrower viewports without overflowing right.
       *
       * The content column carries the app-ground insets and seats the pane
       * card — one rounded surface holding the scroller, so the card's edges
       * stay pinned while the pane scrolls inside it and pane content fills
       * the card's width.
       *
       * At `xl+` with the side panel open this wrapper becomes a flex-row: the
       * panel docks between the nav and the content column, which holds the
       * notices track + card. The scroller's load-bearing props (`relative`,
       * `[overflow-anchor:none]`) travel with the scroller div regardless, so
       * they are unaffected.
       *
       * All three magic numbers come from `navigation/dimensions.ts` — change a
       * value there and the sidebar components, top bar, and these offsets all
       * update together.
       */}
      <div
        style={
          {
            '--nav-top-bar-h': `${MOBILE_TOP_BAR_HEIGHT_PX}px`,
            '--nav-rail-w': `${ICON_RAIL_WIDTH_PX}px`,
            '--nav-full-w': `${MAIN_SIDEBAR_WIDTH_PX}px`,
          } as React.CSSProperties
        }
        className={cx(
          'pt-(--nav-top-bar-h) md:pl-(--nav-rail-w) lg:pl-(--nav-full-w) flex h-full min-w-0 flex-1 md:pt-0',
          panelInColumn ? 'flex-row' : 'flex-col'
        )}
      >
        {panelInColumn && (
          <SidePanel
            content={panelInColumn}
            stage={panelPresence.stage}
            onWidthTransitionEnd={panelPresence.onTransitionEnd}
            isAssistantEnabled={isAssistantEnabled}
            onCloseAlerts={showAssistant}
            onCloseAssistant={toggleAssistant}
          />
        )}
        {/*
         * Content column: notices track + pane card. A flex child so it sits
         * beside the assistant panel when one is shown, and the sole column
         * (full width) otherwise. `min-h-0` keeps the card bounded; `min-w-0`
         * lets panes reflow when the panel narrows the available width. The
         * padding is the card's inset on the app ground; beside the open
         * assistant column (`pr-2`) the two insets sum to the designed 24px
         * seam.
         */}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col p-3 lg:p-4">
          {/*
           * Notices are app-wide banners, not pane content, so they stay pinned
           * above the card rather than scrolling with it.
           */}
          <div className="flex w-full min-w-0 flex-col">
            <ImpersonatorNotice />
            <SyncingNotice />
          </div>
          {/*
           * The pane card: one rounded surface on the app ground holding every
           * routed pane. It wraps the scroller rather than living inside it so
           * its edges stay pinned while the pane content scrolls within —
           * `overflow-clip` rounds the scroller's corners, `min-h-0` + `flex-1`
           * bound it to the column.
           */}
          <PaneCard>
            {/*
             * The single scroll context for the route (app.css keeps html/body
             * locked). It fills the card, so its vertical scrollbar sits at the
             * card's right edge and pane content spans the card's full width
             * (Figma "Context: Fill Space" — panes carry their own 20px
             * insets). The load-bearing scroller classes (`relative`,
             * `[overflow-anchor:none]`) are documented on
             * `PANE_SCROLLER_CLASSNAME`.
             */}
            <div ref={scrollerRef} onScroll={onScroll} className={PANE_SCROLLER_CLASSNAME}>
              <div className="flex h-full w-full min-w-0 flex-col">
                <Outlet />
              </div>
            </div>
          </PaneCard>
        </div>
      </div>
    </>
  );
};

export default HomeScene;
